export type ServiceMock = {
  id: string;
  name: string;
  description: string;
  price: string;
  category: string; // e.g., cleaning, gardening, maintenance, plumber, electrician, hvac, renovation, laundry, pest_control, window_cleaning, carpet_cleaning, waste_removal, moving, disinfection
  tags: string[]; // synonyms/keywords (it/en)
};

export type CompanyMock = {
  id: string;
  name: string;
  image: string;
  rating: number;
  reviewCount: number;
  location: string;
  cities: string[];
  categories: string[];
  tags: string[];
  services: ServiceMock[];
  badges: string[];
  availability: string;
  activeOfferName?: string;
  activeDiscountPercent?: number;
};

// Synonym map to help filters (it/en)
export const CATEGORY_SYNONYMS: Record<string, string[]> = {
  Cleaning: [
    "pulizia", "pulizie", "cleaning", "house cleaning", "uffici", "domestiche",
    "sanificazione", "igienizzazione", "igiene", "housekeeping",
    "pulizia casa", "pulizia appartamento", "pulizie domestiche", "colf", "governante",
    "sgrosso", "post ristrutturazione", "fine lavori", "vetri", "vetrate", "moquette", "tappeti"
  ],
  Gardening: ["giardinaggio", "garden", "lawn", "potatura", "alberi", "prato", "erba", "siepi"],
  Handyman: ["manutenzione", "handyman", "riparazioni", "repair", "fai da te", "montaggi", "bricolage"],
  Painting: ["ristrutturazione", "renovation", "remodel", "rinnovamento", "pittura", "verniciatura", "imbiancatura"],
  Plumbing: ["idraulico", "plumber", "tubi", "perdite", "scarichi", "rubinetti", "impianto idraulico"],
  Electrical: ["elettricista", "electrician", "impianto elettrico", "prese", "luci", "illuminazione", "quadri"],
  Pet_Care: ["caldaia", "clima", "condizionatore", "hvac", "riscaldamento", "aria condizionata", "pet", "animali"],
  Moving: ["traslochi", "moving", "trasporto", "smaltimento ingombranti", "ingombranti", "rifiuti", "junk", "sgombero"],
};

export const MOCK_COMPANIES: CompanyMock[] = [
  {
    id: "c1",
    name: "Crystal Clean Pro",
    image: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=400",
    rating: 4.9,
    reviewCount: 127,
    location: "Centro",
    cities: ["Milano", "Monza"],
    categories: ["cleaning", "window_cleaning", "carpet_cleaning", "disinfection"],
    tags: ["pulizie", "domestiche", "uffici", "vetri", "moquette"],
    services: [
      { id: "s1", name: "Pulizie domestiche", description: "Pulizia profonda appartamenti", price: "€120-180", category: "cleaning", tags: ["pulizie", "domestiche", "cleaning"] },
      { id: "s2", name: "Pulizie uffici", description: "Uffici e spazi commerciali", price: "€150-300", category: "cleaning", tags: ["uffici", "cleaning"] },
      { id: "s3", name: "Lavaggio vetrate", description: "Vetri, vetrine, altezze", price: "€80-200", category: "window_cleaning", tags: ["vetri", "vetrate", "window cleaning"] },
      { id: "s4", name: "Lavaggio moquette", description: "Tessili e tappeti", price: "€90-220", category: "carpet_cleaning", tags: ["moquette", "tappeti", "carpet"] },
      { id: "s5", name: "Sanificazione ambienti", description: "Nebulizzazione certificata", price: "€100-250", category: "disinfection", tags: ["sanificazione", "disinfezione"] },
    ],
    badges: ["Top Rated", "Fast Availability", "Eco-Friendly"],
    availability: "Disponibile oggi",
    activeOfferName: "Spring Clean",
    activeDiscountPercent: 15,
  },
  {
    id: "c2",
    name: "Green Thumb Gardens",
    image: "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=400",
    rating: 4.8,
    reviewCount: 95,
    location: "Periferia",
    cities: ["Milano", "Pavia"],
    categories: ["gardening"],
    tags: ["giardinaggio", "prato", "potatura"],
    services: [
      { id: "s6", name: "Manutenzione prato", description: "Taglio, bordatura, pulizia", price: "€60-100", category: "gardening", tags: ["prato", "giardinaggio", "lawn"] },
      { id: "s7", name: "Progettazione giardino", description: "Landscape design", price: "€300-800", category: "gardening", tags: ["giardino", "design", "garden"] },
      { id: "s8", name: "Potatura alberi", description: "Potature e abbattimenti", price: "€150-400", category: "gardening", tags: ["alberi", "potatura"] },
    ],
    badges: ["Eco-Friendly", "Verified"],
    availability: "Domani pomeriggio",
  },
  {
    id: "c3",
    name: "FixIt Handyman",
    image: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400",
    rating: 4.7,
    reviewCount: 203,
    location: "Area Metropolitana",
    cities: ["Milano", "Bergamo"],
    categories: ["maintenance", "electrician", "plumber"],
    tags: ["manutenzione", "riparazioni", "elettricista", "idraulico"],
    services: [
      { id: "s9", name: "Riparazioni generali", description: "Piccoli lavori e montaggi", price: "€80-200", category: "maintenance", tags: ["manutenzione", "riparazioni", "handyman", "repair"] },
      { id: "s10", name: "Interventi elettrici", description: "Prese, luci, quadri", price: "€90-250", category: "electrician", tags: ["elettricista", "impianto", "electrician"] },
      { id: "s11", name: "Interventi idraulici", description: "Perdite, scarichi", price: "€100-280", category: "plumber", tags: ["idraulico", "perdite", "scarichi", "plumber"] },
    ],
    badges: ["Verified"],
    availability: "Questa settimana",
  },
  {
    id: "c4",
    name: "AquaFlow Plumbing",
    image: "https://images.unsplash.com/photo-1581579188871-c6e902d1eb8c?w=400",
    rating: 4.6,
    reviewCount: 122,
    location: "Nord Città",
    cities: ["Milano", "Como"],
    categories: ["plumber"],
    tags: ["idraulico", "perdite", "scarichi"],
    services: [
      { id: "s12", name: "Riparazione perdite", description: "Ricerca e riparazione", price: "€120-300", category: "plumber", tags: ["idraulico", "perdite"] },
      { id: "s13", name: "Sblocco scarichi", description: "Scarichi otturati", price: "€90-200", category: "plumber", tags: ["scarichi", "tubi"] },
    ],
    badges: ["Fast Availability"],
    availability: "Oggi pomeriggio",
  },
  {
    id: "c5",
    name: "Volt Masters Electric",
    image: "https://images.unsplash.com/photo-1517048676732-d65bc937f952?w=400",
    rating: 4.7,
    reviewCount: 141,
    location: "Est Città",
    cities: ["Milano", "Brescia"],
    categories: ["electrician"],
    tags: ["elettricista", "impianto"],
    services: [
      { id: "s14", name: "Impianti e prese", description: "Adeguamenti e nuove linee", price: "€150-500", category: "electrician", tags: ["elettricista", "impianto", "prese"] },
      { id: "s15", name: "Illuminazione", description: "LED, faretti, esterni", price: "€120-350", category: "electrician", tags: ["luci", "illuminazione"] },
    ],
    badges: ["Top Rated"],
    availability: "In 48h",
  },
  {
    id: "c6",
    name: "CoolBreeze HVAC",
    image: "https://images.unsplash.com/photo-1507668077129-56e32842fceb?w=400",
    rating: 4.5,
    reviewCount: 88,
    location: "Sud Città",
    cities: ["Milano", "Lodi"],
    categories: ["hvac"],
    tags: ["caldaia", "clima", "condizionatore", "aria condizionata"],
    services: [
      { id: "s16", name: "Manutenzione caldaia", description: "Check e bollino", price: "€80-150", category: "hvac", tags: ["caldaia", "manutenzione"] },
      { id: "s17", name: "Installazione clima", description: "Mono e multi split", price: "€300-900", category: "hvac", tags: ["clima", "condizionatore"] },
    ],
    badges: ["Verified"],
    availability: "Questa settimana",
  },
  {
    id: "c7",
    name: "Wash&Fold Express",
    image: "https://images.unsplash.com/photo-1495403919874-8d3e543f9cdf?w=400",
    rating: 4.4,
    reviewCount: 64,
    location: "Centro",
    cities: ["Milano"],
    categories: ["laundry"],
    tags: ["lavanderia", "stiro", "ritiro"],
    services: [
      { id: "s18", name: "Lavaggio e piega", description: "Ritiro e consegna", price: "€25-60", category: "laundry", tags: ["lavanderia", "laundry"] },
      { id: "s19", name: "Lavanderia a secco", description: "Capi delicati", price: "€15-45", category: "laundry", tags: ["dry cleaning", "secco"] },
    ],
    badges: ["Fast Availability"],
    availability: "Oggi",
  },
  {
    id: "c8",
    name: "PestAway Control",
    image: "https://images.unsplash.com/photo-1516738901171-8eb4fc13bd20?w=400",
    rating: 4.6,
    reviewCount: 77,
    location: "Ovest",
    cities: ["Milano", "Novara"],
    categories: ["pest_control"],
    tags: ["disinfestazioni", "insetti", "derattizzazione"],
    services: [
      { id: "s20", name: "Disinfestazione insetti", description: "Zanzare, scarafaggi", price: "€120-260", category: "pest_control", tags: ["disinfestazioni", "insetti", "pest"] },
      { id: "s21", name: "Derattizzazione", description: "Monitoraggio e cattura", price: "€180-380", category: "pest_control", tags: ["derattizzazione", "pest"] },
    ],
    badges: ["Verified"],
    availability: "In 72h",
  },
  {
    id: "c9",
    name: "JunkAway Disposal",
    image: "https://images.unsplash.com/photo-1581093809061-ff8d057f1a7a?w=400",
    rating: 4.3,
    reviewCount: 58,
    location: "Periferia",
    cities: ["Milano", "Rho"],
    categories: ["waste_removal", "moving"],
    tags: ["ingombranti", "rifiuti", "sgombero", "traslochi"],
    services: [
      { id: "s22", name: "Ritiro ingombranti", description: "Mobili, elettrodomestici", price: "€80-220", category: "waste_removal", tags: ["ingombranti", "rifiuti", "junk"] },
      { id: "s23", name: "Traslochi piccoli", description: "Monolocali, uffici", price: "€180-450", category: "moving", tags: ["traslochi", "moving"] },
    ],
    badges: ["Fast Availability"],
    availability: "Domani mattina",
  },
  {
    id: "c10",
    name: "Master Renovations",
    image: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400",
    rating: 4.7,
    reviewCount: 203,
    location: "Area Metropolitana",
    cities: ["Milano", "Varese"],
    categories: ["renovation"],
    tags: ["ristrutturazione", "remodel", "bagno", "cucina"],
    services: [
      { id: "s24", name: "Rifacimento bagno", description: "Completo, impianti compresi", price: "€2.500-8.000", category: "renovation", tags: ["bagno", "ristrutturazione", "bathroom remodel"] },
      { id: "s25", name: "Aggiornamento cucina", description: "Mobili, piani, impianti", price: "€1.500-5.000", category: "renovation", tags: ["cucina", "renovation"] },
      { id: "s26", name: "Riparazioni generali", description: "Cartongesso, tinteggiature", price: "€100-500", category: "maintenance", tags: ["riparazioni", "manutenzione"] },
    ],
    badges: ["Top Rated", "Verified"],
    availability: "Questa settimana",
  },
];

// Basic normalization utilities to improve matching robustness
function normalizeText(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // strip accents
    .replace(/[^a-z0-9_ ]/g, " ") // keep basic letters/numbers/underscore for cats
    .replace(/\s+/g, " ")
    .trim();
}

const STOPWORDS_IT = new Set([
  "di","del","della","dei","degli","delle","dell","dello",
  "il","lo","la","i","gli","le",
  "un","uno","una",
  "per","a","al","allo","alla","ai","agli","alle","da","dal","dallo","dalla","dai","dagli","dalle",
  "e","ed","con","su","sul","sullo","sulla","sui","sugli","sulle",
  "de","d","l","all","dell"
]);

const GENERIC_SERVICE_WORDS = new Set(["servizio","servizi","lavoro","lavori"]);

// Very light Italian stemming for common endings to unify forms (heuristic)
function stemIt(word: string): string {
  let w = word;
  // remove plural/singular simple endings
  w = w.replace(/(mente|mente$)/g, ""); // avverbi
  w = w.replace(/(azioni|azione|zioni|zione)$/g, "zion");
  w = w.replace(/(mente)$/g, "");
  w = w.replace(/(izzare|izzati|izzata|izzato|izzazioni|izzazione)$/g, "izz");
  w = w.replace(/(mente)$/g, "");
  w = w.replace(/(issimi|issima|issimo|mente|mente)$/g, "");
  w = w.replace(/(atori|atore|atrici|atrice)$/g, "ator");
  w = w.replace(/(zioni|zione)$/g, "zion");
  w = w.replace(/(sioni|sione)$/g, "sion");
  w = w.replace(/(amenti|amento)$/g, "ament");
  w = w.replace(/(imenti|imento)$/g, "iment");
  w = w.replace(/(mente)$/g, "");
  // vowels and plural
  w = w.replace(/(i|e|o|a)$/g, "");
  return w;
}

function stemTokens(tokens: string[]): string[] {
  return tokens.map(stemIt).filter(Boolean);
}

function toTokens(s: string): string[] {
  const n = normalizeText(s);
  return n
    .split(" ")
    .filter((w) => w && !STOPWORDS_IT.has(w) && !GENERIC_SERVICE_WORDS.has(w));
}

// Simple Levenshtein distance for fuzzy tolerance on short tokens
function lev(a: string, b: string): number {
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,      // deletion
        dp[i][j - 1] + 1,      // insertion
        dp[i - 1][j - 1] + cost // substitution
      );
    }
  }
  return dp[m][n];
}

function fuzzyIncludes(termTokensStem: string[], synTokensStem: string[]): boolean {
  // consider matched if every syn token has a close match in term tokens
  return synTokensStem.every((synTok) => {
    return termTokensStem.some((t) => {
      if (t === synTok) return true;
      if (Math.abs(t.length - synTok.length) > 2) return false;
      return lev(t, synTok) <= 1; // allow one edit
    });
  });
}

export function mapSearchTermToCategories(term: string): string[] {
  const t = term ? term.trim() : "";
  if (!t) return [];
  const termTokens = toTokens(t);
  const termTokensStem = stemTokens(termTokens);
  const termNorm = normalizeText(t);
  const hits: string[] = [];
  for (const [cat, synonyms] of Object.entries(CATEGORY_SYNONYMS)) {
    const catHit = synonyms.some((syn) => {
      const synTokens = toTokens(syn);
      const synTokensStem = stemTokens(synTokens);
      const synNorm = normalizeText(syn);
      // Strategy:
      // 1) phrase includes after normalization
      if (termNorm.includes(synNorm)) return true;
      // 2) all synonym tokens present in term tokens
      if (synTokens.length > 0) {
        const ok = synTokens.every((tok) => termTokens.includes(tok));
        if (ok) return true;
      }
      // 3) stemmed fuzzy match (tollerates plural/singular and small typos)
      if (synTokensStem.length > 0 && fuzzyIncludes(termTokensStem, synTokensStem)) return true;
      return false;
    });
    if (catHit) hits.push(cat);
  }
  return hits;
}


